import { crv, cva } from '@/styled-system/css';

export const buttonRecipe = cva({
  base: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transitionProperty: {
      _motionReduce: 'none',
      _motionSafe: 'box-shadow, background-color',
    },
    transitionDuration: {
      _motionReduce: '0s',
      _motionSafe: '{durations.200}',
    },
    transitionTimingFunction: '{easings.ease}',
  },
  variants: {
    tone: {
      'brand': {},
      'neutral': {},
      'neutral-fixed': {},
      'critical': {},
    },

    emphasis: {
      bare: {
        focusOutline: true,
      },
      filled: {
        boxShadow: {
          base: `inset 0 0 0 {borderWidths.default} var(--shadow-color)`,
          _focusVisible: `inset 0 0 0 {borderWidths.default} var(--shadow-color), {shadows.focus}`,
        },
      },
      outlined: {
        boxShadow: {
          base: `inset 0 0 0 {borderWidths.default} var(--shadow-color)`,
          _focusVisible: `inset 0 0 0 {borderWidths.default} var(--shadow-color), {shadows.focus}`,
        },
      },
    },

    disabled: {
      true: {},
      false: {},
    },

    loading: {
      true: {},
      false: {},
    },

    ...crv('fluid', {
      true: {
        width: '100%',
      },
      false: {
        width: 'auto',
      },
    }),

    onImpact: {
      true: {},
      false: {},
    },

    size: {
      sm: {
        borderRadius: 'obj.default',
        gap: 'tighter',
        paddingBlock: 'Button.sm.padding.vert',
        paddingInline: 'default',
      },
      md: {
        borderRadius: 'obj.default',
        gap: 'tighter',
        paddingBlock: 'Button.md.padding.vert',
        paddingInline: 'default',
      },
      lg: {
        borderRadius: 'obj.default',
        gap: 'tighter',
        paddingBlock: 'default',
        paddingInline: 'default',
      },
    },
  },
  compoundVariants: [
    {
      loading: false,
      disabled: false,
      css: {
        cursor: 'pointer',
      },
    },
    {
      loading: false,
      disabled: true,
      css: {
        opacity: 'disabled',
      },
    },
    {
      size: ['sm'],
      css: {
        _after: {
          content: '""',
          position: 'absolute',
          height: 'tapArea.mobile.default',
          minWidth: 'tapArea.mobile.default',
          width: '100%',
          left: '0',
          top: '50%',
          marginTop: 'calc(({sizes.tapArea.mobile.default} / 2) * -1)',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'bg.action.bare.neutral.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'bg.action.bare.neutral.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'bg.action.bare.neutral.default',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'bg.action.bare.neutral-fixed.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'bg.action.hero.default',
        },
        boxShadowColor: {
          base: 'border.invisible',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'bg.action.critical.hero.default',
        },
        boxShadowColor: {
          base: 'border.invisible',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'bg.action.neutral.alt.default',
        },
        boxShadowColor: {
          base: 'border.action.hero.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'bg.action.critical.neutral.default',
        },
        boxShadowColor: {
          base: 'border.action.critical.hero.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'Button.outlined.neutral.bg.default',
        },
        boxShadowColor: {
          base: 'border.action.neutral.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'bg.action.neutral-fixed.default',
        },
        boxShadowColor: {
          base: 'border.action.neutral-fixed.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'bg.action.neutral.alt.default',
        },
        boxShadowColor: {
          base: 'border.invisible',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'bg.action.impact.bare.default',
        },
        boxShadowColor: {
          base: 'border.onImpact.neutral',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        boxShadowColor: {
          base: 'border.onImpact.action.hero.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'bare',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'bare',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'bg.action.critical.neutral.default',
        },
        boxShadowColor: {
          base: 'border.invisible',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        boxShadowColor: {
          base: 'border.onImpact.action.critical.hero.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'bare',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'bare',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'bg.action.neutral-fixed.default',
        },
        boxShadowColor: {
          base: 'border.invisible',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'bg.invisible',
        },
        boxShadowColor: {
          base: 'border.onImpact.neutral-fixed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'brand',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.bare.neutral.hover',
          _active: 'bg.action.bare.neutral.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'critical',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.bare.neutral.hover',
          _active: 'bg.action.bare.neutral.pressed',
        },
      },
    },
    {
      emphasis: 'bare',
      tone: 'neutral-fixed',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.bare.neutral-fixed.hover',
          _active: 'bg.action.bare.neutral-fixed.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.hero.hover',
          _active: 'bg.action.hero.pressed',
        },
        boxShadowColor: {
          _hover: 'border.invisible',
          _active: 'border.invisible',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.hero.hover',
          _active: 'bg.action.critical.hero.pressed',
        },
        boxShadowColor: {
          _hover: 'border.invisible',
          _active: 'Button.filled.critical.border.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.alt.hover',
          _active: 'bg.action.neutral.alt.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.hero.hover',
          _active: 'border.action.hero.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.critical.hero.hover',
          _active: 'border.action.critical.hero.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.hover',
          _active: 'bg.action.neutral.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.neutral.hover',
          _active: 'border.action.neutral.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral-fixed.hover',
          _active: 'bg.action.neutral-fixed.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.neutral-fixed.hover',
          _active: 'border.action.neutral-fixed.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'brand',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.alt.hover',
          _active: 'bg.action.neutral.alt.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'neutral',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.impact.bare.hover',
          _active: 'bg.action.impact.bare.pressed',
        },
        boxShadowColor: {
          _hover: 'border.onImpact.neutral',
          _active: 'border.onImpact.neutral',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'brand',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.alt.hover',
          _active: 'bg.action.neutral.alt.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.hero.hover',
          _active: 'border.action.hero.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'critical',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'critical',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
        boxShadowColor: {
          _hover: 'border.action.critical.hero.hover',
          _active: 'border.action.critical.hero.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'neutral-fixed',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral-fixed.hover',
          _active: 'bg.action.neutral-fixed.pressed',
        },
        boxShadowColor: {
          _hover: 'border.invisible',
          _active: 'border.invisible',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.impact.bare.fixed.hover',
          _active: 'bg.action.impact.bare.fixed.pressed',
        },
        boxShadowColor: {
          _hover: 'border.neutral-fixed',
          _active: 'border.neutral-fixed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'bare',
      tone: 'brand',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.neutral.alt.hover',
          _active: 'bg.action.neutral.alt.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'bare',
      tone: 'critical',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.critical.neutral.hover',
          _active: 'bg.action.critical.neutral.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'bare',
      tone: 'neutral',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.impact.bare.hover',
          _active: 'bg.action.impact.bare.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'bare',
      tone: 'neutral-fixed',
      loading: false,
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'bg.action.impact.bare.fixed.hover',
          _active: 'bg.action.impact.bare.fixed.pressed',
        },
      },
    },
  ],
});
